import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from "@angular/core";

import { computeDueInState, DueInState } from "./due-in-state";

/**
 * Renders an order's SLA due-by status as `"Due in Xd Xh"`,
 * `"Overdue Xh"`, or `"—"`. Shared by the kanban card, dashboard
 * table, and order detail page so all three sites render identically
 * (PRD 0022).
 *
 * Renders the **pause-aware** `effectiveDeadlineUtc` when supplied, falling
 * back to the raw `dueDateUtc` otherwise. A paused order's `effectiveDeadlineUtc`
 * has the accumulated (and live) pause segment folded in, so the countdown shows
 * frozen "time remaining" instead of marching past the raw deadline (R3 slice
 * 05). A `null` / `undefined` effective deadline falls through to `dueDateUtc`;
 * when both are absent the readout is `—` with no coloured state class.
 *
 * Use:
 *   <app-due-in
 *     [dueDateUtc]="order().dueDateUtc"
 *     [effectiveDeadlineUtc]="order().effectiveDeadlineUtc"
 *   ></app-due-in>
 */
@Component({
  selector: "app-due-in",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<span [class]="renderedClass()">{{ label() }}</span>`,
  styles: [
    `
      .due-in {
        font-size: 12px;
        font-weight: 500;
      }

      .due-in--overdue {
        color: #c62828;
      }

      .due-in--soon {
        color: #e65100;
      }

      .due-in--later {
        color: rgba(0, 0, 0, 0.54);
      }
    `,
  ],
})
export class DueInComponent {
  readonly dueDateUtc = input<string | null | undefined>(null);
  /**
   * Pause-aware SLA deadline. Takes precedence over `dueDateUtc` when present;
   * `null` / `undefined` falls back to the raw `dueDateUtc`.
   */
  readonly effectiveDeadlineUtc = input<string | null | undefined>(null);

  protected readonly render = computed(() =>
    computeDueInState(this.effectiveDeadlineUtc() ?? this.dueDateUtc()),
  );
  protected readonly label = computed(() => this.render().label);
  protected readonly renderedClass = computed(() => {
    const state = this.render().state;
    if (state === DueInState.Unknown) return "due-in";
    return `due-in due-in--${state}`;
  });
}
